import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MapPin, AlertTriangle, Play, CheckCheck, XCircle, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Page, Loading, ErrorBox } from "@/components/site/Common";
import { TradeBadge, StatusBadge } from "@/components/site/Badges";
import { Box, QuotesList, QuoteForm, DirectRespond, InvoiceGenerator, useAction } from "@/components/job/JobActions";
import { Chat, InvoiceBox } from "@/components/job/JobChatInvoice";
import { AppointmentPanel } from "@/components/job/Appointment";
import { QualityForm, QualityView, IncidentDialog } from "@/components/job/JobQuality";
import { useMe } from "@/hooks/useMe";
import { get, post, hzFile } from "@/lib/api";
import { eur, fdate } from "@/lib/hz";

const CHAT_STATES = ["asignado", "en_curso", "completado", "facturado", "pagado"];

const ProSide = ({ job, me }) => {
  const { busy, run } = useAction();
  const mine = job.pro_id === me.id;
  return (
    <>
      {job.status === "abierto" && !mine && (job.my_quote
        ? <Box title="Tu presupuesto" testid="my-quote"><div className="font-heading text-2xl font-extrabold">{eur(job.my_quote.amount)}</div><div className="text-sm text-slate-500">{job.my_quote.days} días · {job.my_quote.status}</div></Box>
        : <Box title="Enviar presupuesto"><QuoteForm jobId={job.id} /></Box>)}
      {job.status === "solicitado" && mine && <Box title="Solicitud directa"><DirectRespond job={job} /></Box>}
      {job.status === "asignado" && mine && <Button size="lg" className="w-full rounded-xl" disabled={!!busy} data-testid="start-job-btn" onClick={() => run("s", () => post(`/jobs/${job.id}/start`), "Trabajo iniciado")}>{busy ? <Loader2 className="animate-spin" /> : <Play />}Iniciar trabajo</Button>}
      {job.status === "en_curso" && mine && <Button size="lg" className="w-full rounded-xl" disabled={!!busy} data-testid="complete-job-btn" onClick={() => run("c", () => post(`/jobs/${job.id}/complete`), "Trabajo marcado como completado")}>{busy ? <Loader2 className="animate-spin" /> : <CheckCheck />}Marcar como completado</Button>}
      {job.status === "completado" && mine && !job.invoice_id && <Box title="Emitir factura"><InvoiceGenerator job={job} /></Box>}
    </>
  );
};

const ClientSide = ({ job }) => {
  const { busy, run } = useAction();
  const done = ["completado", "facturado", "pagado"].includes(job.status);
  return (
    <>
      {job.status === "abierto" && <Box title={`Presupuestos (${job.quotes?.length ?? 0})`}><QuotesList job={job} canAccept /></Box>}
      {job.status === "solicitado" && <Box><p className="text-sm text-slate-600">Esperando respuesta de {job.pro_name}.</p></Box>}
      {done && !job.quality && <QualityForm jobId={job.id} />}
      {done && <IncidentDialog jobId={job.id} />}
      {["abierto", "solicitado", "asignado"].includes(job.status) && (
        <Button variant="ghost" className="w-full text-slate-500" disabled={!!busy} data-testid="cancel-job-btn" onClick={() => run("x", () => post(`/jobs/${job.id}/cancel`), "Trabajo cancelado")}><XCircle />Cancelar trabajo</Button>
      )}
    </>
  );
};

export default function JobDetail() {
  const { id } = useParams();
  const { data: me } = useMe();
  const { data: job, isLoading, isError } = useQuery({ queryKey: ["jobs", id], queryFn: () => get(`/jobs/${id}`) });
  if (isLoading) return <Page><Loading /></Page>;
  if (isError || !job) return <Page><ErrorBox text="No encontramos este trabajo o no tienes acceso." /></Page>;
  const isClient = me.role === "cliente";
  const other = isClient ? job.pro_name : job.client_name;
  const phone = isClient ? job.pro_phone : job.client_phone;
  return (
    <Page>
      <Link to="/panel" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="size-4" />Mi panel</Link>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="job-detail">
            <div className="flex flex-wrap items-center gap-2">
              <TradeBadge trade={job.trade} /><StatusBadge status={job.status} />
              {job.urgency === "urgente" && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700"><AlertTriangle className="size-3" />Urgente</span>}
            </div>
            <h1 className="mt-4 font-heading text-3xl font-extrabold text-slate-900" data-testid="job-title">{job.title}</h1>
            <p className="mt-3 whitespace-pre-line text-slate-700">{job.description}</p>
            <div className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
              <div className="flex items-center gap-1.5 text-slate-600"><MapPin className="size-4" />{job.address || job.city}</div>
              <div className="text-slate-600">Publicado el {fdate(job.created_at)}</div>
              <div className="text-slate-600">Modalidad: {job.mode === "directo" ? "solicitud directa" : "presupuestos"}</div>
              <div className="text-slate-600">{job.price != null ? <>Precio acordado: <b>{eur(job.price)}</b></> : job.budget != null ? <>Presupuesto máx.: <b>{eur(job.budget)}</b></> : "Sin presupuesto indicado"}</div>
            </div>
            {!!job.photos?.length && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="job-photos">
                {job.photos.map((p) => <a key={p} href={hzFile(`/jobs/${job.id}/photos/${p}`)} target="_blank" rel="noreferrer"><img src={hzFile(`/jobs/${job.id}/photos/${p}`)} alt="Foto del trabajo" className="size-24 rounded-xl object-cover" /></a>)}
              </div>
            )}
            {job.reassign_alert && <div className="mt-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">El profesional no se presentó. Estamos asignando un sustituto con urgencia.</div>}
          </div>
          {job.pro_id && CHAT_STATES.includes(job.status) && <Chat jobId={job.id} meId={me.id} otherName={other} />}
          {job.invoice_id && <InvoiceBox invoiceId={job.invoice_id} canPay={isClient} />}
        </div>
        <aside className="space-y-4">
          {other && (
            <Box testid="job-counterpart">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{isClient ? "Profesional" : "Cliente"}</div>
              <div className="mt-1 font-heading text-lg font-bold">{other}</div>
              {phone && <a href={`tel:${phone}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600"><Phone className="size-4" />{phone}</a>}
            </Box>
          )}
          {job.pro_id && ["asignado", "en_curso", "completado"].includes(job.status) && <AppointmentPanel jobId={job.id} isPro={!isClient} canBook={["asignado", "en_curso"].includes(job.status)} />}
          {isClient ? <ClientSide job={job} /> : <ProSide job={job} me={me} />}
          {job.quality && <QualityView q={job.quality} />}
          {!!job.incidents?.length && (
            <Box title="Incidencias" testid="job-incidents">
              {job.incidents.map((i) => <div key={i.id} className="border-b border-slate-100 py-2 text-sm last:border-0"><b>{i.category}</b> · <span className="capitalize">{i.status}</span>{i.resolution && <div className="text-slate-500">{i.resolution}</div>}</div>)}
            </Box>
          )}
        </aside>
      </div>
    </Page>
  );
}
